import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { inputCls, Field } from "./Projects";
import { toast } from "sonner";
import {
  X, Save, Send, Check, XCircle, HelpCircle, Pause, GitBranch,
  Paperclip, Trash2, Upload, ClipboardList, History, ChevronDown,
  User as UserIcon, MessageSquare, FileDown, GitCompare,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import { SearchableSelect } from "@/components/SearchableSelect";

/**
 * ONE Note — Detail / Create / Edit / Action panel.
 *
 * Rendered as a right-side slide-over from the OneNotes list. Handles:
 *   - New note (id=null, editable form, POST /one, then POST /one/{id}/submit)
 *   - Existing Draft (creator edits + submits)
 *   - Pending / Hold (current approver can approve / reject / hold / query)
 *   - Query Pending (recipient replies)
 *   - Approved (view + optionally create-revision if Policy Making)
 *   - Rejected / Superseded (read-only)
 */

const MIME_ALLOW = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg", "image/jpg", "image/png",
];

const ATTACH_MAX_COUNT = 3;
const ATTACH_MAX_BYTES = 500 * 1024;

const STATUS_TERMINAL = new Set(["Approved", "Rejected", "Superseded"]);

function useMe() {
  const { user } = useAuth();
  return user || {};
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => {
      // reader.result = "data:<mime>;base64,AAAA…" — strip the prefix
      const s = String(reader.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function StatusRibbon({ status, current, total }) {
  const map = {
    Draft: "bg-slate-100 text-slate-700 border-slate-300",
    Pending: "bg-amber-50 text-amber-800 border-amber-300",
    Hold: "bg-orange-50 text-orange-800 border-orange-300",
    "Query Pending": "bg-violet-50 text-violet-800 border-violet-300",
    "Query Replied": "bg-sky-50 text-sky-800 border-sky-300",
    Approved: "bg-emerald-50 text-emerald-800 border-emerald-300",
    Rejected: "bg-rose-50 text-rose-800 border-rose-300",
    Superseded: "bg-slate-100 text-slate-500 border-slate-300",
  };
  return (
    <div className={`inline-flex items-center gap-2 border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${map[status] || map.Draft}`}>
      <span>{status}</span>
      {total && !STATUS_TERMINAL.has(status) && status !== "Draft" && (
        <span className="opacity-70 font-mono">· L{current || 1}/{total}</span>
      )}
    </div>
  );
}

function ApproversStrip({ approvers, currentLevel, status, actions = [] }) {
  // Build a quick lookup: level → last action ("approve"/"reject"/"skip"/"hold").
  // Used to badge skipped levels distinctly from the rest of the chain.
  const actionByLevel = {};
  for (const a of actions) {
    actionByLevel[a.level] = a.action;
  }
  return (
    <div className="flex items-center flex-wrap gap-2" data-testid="one-approvers-strip">
      {approvers.map((a, i) => {
        const lvl = a.level;
        const act = actionByLevel[lvl];
        const skipped = act === "skip";
        const done = !skipped && (status === "Approved" || (lvl < currentLevel && act === "approve"));
        const active = !STATUS_TERMINAL.has(status) && status !== "Draft" && lvl === currentLevel && !skipped;
        let cls = "bg-white border-slate-300 text-slate-600";
        if (skipped) cls = "bg-slate-100 border-slate-400 text-slate-500 line-through";
        else if (status === "Rejected" && active) cls = "bg-rose-50 border-rose-300 text-rose-800";
        else if (done) cls = "bg-emerald-50 border-emerald-300 text-emerald-800";
        else if (active) cls = "bg-amber-50 border-amber-400 text-amber-900 ring-2 ring-amber-200";
        return (
          <div key={i} className="flex items-center gap-1">
            <div className={`px-2 py-1 border text-[11px] font-bold ${cls}`}
                 title={skipped ? `Skipped — L${lvl}` : a.user_email}
                 data-testid={skipped ? `one-approver-skipped-L${lvl}` : undefined}>
              L{lvl} · {a.user_name || a.user_email}
              {skipped && <span className="ml-1 text-[9px] uppercase tracking-wider text-purple-700 not-italic no-underline">skipped</span>}
            </div>
            {i < approvers.length - 1 && <span className="text-slate-400 text-[10px]">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function AttachmentRow({ att, onRemove, canRemove }) {
  const kb = ((att.size || 0) / 1024).toFixed(0);
  const dataUrl = `data:${att.content_type};base64,${att.data_base64}`;
  return (
    <div className="flex items-center justify-between border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <Paperclip size={12} className="text-slate-500 shrink-0" />
        <a href={dataUrl} download={att.name} className="text-slate-900 hover:text-[#F97316] truncate" title={att.name}>
          {att.name}
        </a>
        <span className="text-slate-500 shrink-0">{kb} KB</span>
      </div>
      {canRemove && (
        <button
          type="button"
          data-testid={`one-att-remove-${att.name}`}
          onClick={onRemove}
          className="text-slate-500 hover:text-rose-600 shrink-0"
          title="Remove attachment"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

function ActionRow({ action, idx }) {
  return (
    <div className="border-b border-slate-100 py-2 flex items-start gap-3 text-xs" data-testid={`one-action-${idx}`}>
      <div className="w-10 shrink-0 font-mono font-bold text-slate-500">L{action.level}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${
            action.action === "approve" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
              : action.action === "reject" ? "bg-rose-50 text-rose-800 border-rose-300"
              : action.action === "skip" ? "bg-slate-100 text-slate-600 border-slate-300"
              : "bg-amber-50 text-amber-800 border-amber-300"
          }`}>
            {action.action}
          </span>
          <span className="text-slate-700">{action.by}</span>
          <span className="text-slate-500 text-[10px] ml-auto">{new Date(action.at).toLocaleString()}</span>
        </div>
        {action.remarks && <div className="text-slate-700 mt-1 italic">&ldquo;{action.remarks}&rdquo;</div>}
      </div>
    </div>
  );
}

function QueryRow({ q, idx, approvers, initiatorName }) {
  const raiser = approvers.find(a => a.level === q.raised_by_level);
  const target = approvers.find(a => a.level === q.raised_to_level);
  const targetLabel = q.raised_to_level === 0
    ? `Initiator (${initiatorName || "—"})`
    : `L${q.raised_to_level} (${target?.user_name || "—"})`;
  return (
    <div className="border border-violet-200 bg-violet-50/50 p-3 text-xs" data-testid={`one-query-${idx}`}>
      <div className="flex items-center gap-2 text-[11px] font-bold text-violet-900">
        <MessageSquare size={12} />
        L{q.raised_by_level} ({raiser?.user_name || "—"}) → {targetLabel}
      </div>
      <div className="mt-1 text-slate-800"><span className="font-bold text-violet-800">Q:</span> {q.question}</div>
      {q.reply ? (
        <div className="mt-1 text-slate-800"><span className="font-bold text-sky-800">A:</span> {q.reply}</div>
      ) : (
        <div className="mt-1 text-[10px] text-violet-700 italic">Awaiting reply…</div>
      )}
      <div className="mt-1 text-[10px] text-slate-500">
        raised {new Date(q.raised_at).toLocaleString()}
        {q.replied_at && ` · replied ${new Date(q.replied_at).toLocaleString()}`}
      </div>
    </div>
  );
}

function ApproversPicker({ branchId, value, onChange, disabled }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branchId) { setCandidates([]); return; }
    setLoading(true);
    api.get("/one/meta/approver-candidates", { params: { branch_id: branchId } })
      .then(r => setCandidates(r.data?.rows || []))
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
  }, [branchId]);

  // Auto-seed an empty L1 row the first time a branch is picked, so the
  // user immediately sees a picker to fill in.
  useEffect(() => {
    if (branchId && !disabled && value.length === 0) {
      onChange([{ level: 1, user_id: "", user_email: "", user_name: "" }]);
    }
  }, [branchId, disabled, value, onChange]);

  function setLevel(level, userId) {
    const chosen = candidates.find(c => c.id === userId);
    const others = value.filter(a => a.level !== level);
    if (!userId) return onChange(others);
    onChange([...others, {
      level,
      user_id: chosen.id,
      user_email: chosen.email || "",
      user_name: chosen.name || "",
    }].sort((a, b) => a.level - b.level));
  }

  function addLevel() {
    const next = (value[value.length - 1]?.level || 0) + 1;
    if (next > 5) return;
    onChange([...value, { level: next, user_id: "", user_email: "", user_name: "" }]);
  }

  function removeLevel(level) {
    // Only allow removing the LAST level so we keep contiguous L1..Ln
    if (level !== value[value.length - 1]?.level) return;
    onChange(value.filter(a => a.level !== level));
  }

  const rows = [...value].sort((a, b) => a.level - b.level);
  return (
    <div data-testid="one-approvers-picker">
      {!branchId ? (
        <div className="text-xs text-slate-500 italic border border-slate-200 bg-slate-50 px-3 py-2">
          Pick a branch first.
        </div>
      ) : loading ? (
        <div className="text-xs text-slate-500 italic">Loading candidates…</div>
      ) : candidates.length === 0 ? (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2">
          No eligible users for this branch. Ask admin to grant a user access to this branch.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.level} className="grid grid-cols-[52px_1fr_auto] gap-2 items-center">
              <div className="text-[11px] font-bold text-slate-600 border border-slate-300 bg-slate-50 px-2 py-2 text-center">
                L{row.level}
              </div>
              <SearchableSelect
                testid={`one-approver-L${row.level}`}
                items={candidates}
                value={row.user_id || ""}
                onChange={(id) => setLevel(row.level, id || "")}
                getKey={(c) => c.id}
                getLabel={(c) => c.name || c.email}
                getSubLabel={(c) => c.email || ""}
                getSearchText={(c) => `${c.name || ""} ${c.email || ""}`}
                placeholder="— Select approver —"
                searchPlaceholder="Search by name or email…"
                emptyText="No matching users"
                disabled={disabled}
              />
              {!disabled && row.level === rows[rows.length - 1]?.level && rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLevel(row.level)}
                  className="text-slate-500 hover:text-rose-600 px-2 py-2 border border-slate-300 hover:border-rose-300"
                  title="Remove this level"
                  data-testid={`one-approver-remove-L${row.level}`}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
          {!disabled && rows.length < 5 && (
            <button
              type="button"
              data-testid="one-approver-add"
              onClick={addLevel}
              className="text-xs text-[#F97316] hover:text-[#EA580C] font-bold uppercase tracking-wider"
            >
              + Add another level (max 5)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function OneNotesDetail({ id, onClose, onSaved, onViewList }) {
  const me = useMe();
  const isNew = !id;
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ Financial: {}, "Non-Financial": {}, "Policy Making": {}, Other: {} });

  // Form state for CREATE / EDIT DRAFT
  const [form, setForm] = useState({
    category: "Non-Financial",
    branch_id: "",
    branch_name: "",
    subject: "",
    body_html: "",
    amount: 0,
    project_id: "",
    project_name: "",
    approvers: [],
    attachments: [],
  });

  // Action state
  const [remarks, setRemarks] = useState("");
  const [queryTarget, setQueryTarget] = useState({ level: 0, user_id: "" });
  const [queryText, setQueryText] = useState("");
  const [queryReply, setQueryReply] = useState("");
  const [revisionReason, setRevisionReason] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [compareData, setCompareData] = useState(null);  // {left, right, root_id}

  // Load meta once
  useEffect(() => {
    api.get("/one/meta/categories").then(r => {
      const m = {}; r.data.forEach(x => { m[x.category] = x; });
      setMeta(m);
    }).catch(() => {});
    api.get("/branches").then(r => setBranches(r.data || [])).catch(() => {});
  }, []);

  // Load projects when branch is picked and category is Financial
  useEffect(() => {
    if (!form.branch_id) return;
    api.get("/projects-picker", { params: { branch_id: form.branch_id } })
      .then(r => setProjects(r.data || []))
      .catch(() => setProjects([]));
  }, [form.branch_id]);

  // Load doc on mount when id is provided
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/one/${id}`)
      .then(r => {
        setDoc(r.data);
        setForm({
          category: r.data.category,
          branch_id: r.data.branch_id,
          branch_name: r.data.branch_name || "",
          subject: r.data.subject,
          body_html: r.data.body_html || "",
          amount: r.data.amount || 0,
          project_id: r.data.project_id || "",
          project_name: r.data.project_name || "",
          approvers: r.data.approvers || [],
          attachments: r.data.attachments || [],
        });
      })
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load note"))
      .finally(() => setLoading(false));
  }, [id]);

  const categoryCfg = meta[form.category] || {};
  const isDraft = !doc || doc.status === "Draft";
  const isCreatorOfDraft = isNew || (doc?.status === "Draft" && (
    doc?.initiated_by_user_id === me.id || doc?.initiated_by_email === me.email
  ));
  // 2026-Feb: upper-level approvers may skip lower levels. `myActingLevel`
  // is 0 when the current user cannot act; otherwise it's the LOWEST
  // approver level they are registered at that is >= doc.current_level.
  // Admins who are NOT explicit approvers fall back to acting at
  // doc.current_level (the classic admin bypass).
  const myActingLevel = useMemo(() => {
    if (!doc) return 0;
    if (STATUS_TERMINAL.has(doc.status)) return 0;
    if (doc.status === "Query Pending") return 0;
    const cur = Number(doc.current_level || 1);
    const uid = me.id;
    const email = me.email;
    const myLevels = (doc.approvers || [])
      .filter(a => a.user_id === uid || a.user_email === email
                   || a.user_id === email || a.user_email === uid)
      .map(a => Number(a.level || 0))
      .sort((x, y) => x - y);
    if (myLevels.length > 0) {
      const eligible = myLevels.filter(lvl => lvl >= cur);
      return eligible.length ? eligible[0] : 0;
    }
    if (me.role === "admin") return cur;
    return 0;
  }, [doc, me]);
  const isCurrentApprover = myActingLevel > 0;
  const isQueryRecipient = useMemo(() => {
    if (!doc || doc.status !== "Query Pending") return false;
    const q = (doc.queries || []).find(x => x.id === doc.active_query_id);
    if (!q) return false;
    return q.raised_to_user_id === me.id || q.raised_to_user_id === me.email || me.role === "admin";
  }, [doc, me]);
  const canCreateRevision = doc?.status === "Approved" && doc?.is_active
    && (meta[doc.category]?.revision_allowed);

  async function pickBranch(bid) {
    const b = branches.find(x => x.id === bid);
    setForm(f => ({
      ...f,
      branch_id: bid,
      branch_name: b?.branch_name || "",
      project_id: "",
      project_name: "",
      approvers: [],   // approvers reset on branch change (branch-scoped picker)
    }));
  }

  function pickProject(pid) {
    const p = projects.find(x => x.id === pid);
    setForm(f => ({ ...f, project_id: pid, project_name: p?.project_name || "" }));
  }

  async function onPickFiles(evt) {
    const files = Array.from(evt.target.files || []);
    evt.target.value = "";  // reset
    if (!files.length) return;
    const current = form.attachments || [];
    if (current.length + files.length > ATTACH_MAX_COUNT) {
      toast.error(`Maximum ${ATTACH_MAX_COUNT} attachments.`);
      return;
    }
    const nextAtt = [...current];
    for (const f of files) {
      if (f.size > ATTACH_MAX_BYTES) {
        toast.error(`"${f.name}" exceeds 500 KB.`);
        continue;
      }
      if (!MIME_ALLOW.includes(f.type)) {
        toast.error(`"${f.name}" — unsupported type. Use PDF / XLSX / DOCX / JPG / PNG.`);
        continue;
      }
      const b64 = await fileToBase64(f);
      nextAtt.push({ name: f.name, content_type: f.type, size: f.size, data_base64: b64 });
    }
    setForm({ ...form, attachments: nextAtt });
  }

  function removeAttachment(idx) {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  }

  function _validateForm() {
    if (!form.subject.trim()) { toast.error("Subject is required."); return false; }
    if (!form.branch_id)      { toast.error("Branch is required."); return false; }
    if (categoryCfg.amount_required && !(Number(form.amount) > 0)) {
      toast.error("Financial category requires an Approval Amount > 0."); return false;
    }
    if (categoryCfg.project_required && !form.project_id) {
      toast.error("Financial category requires a Project."); return false;
    }
    if (!form.approvers.length) { toast.error("At least one approver (L1) is required."); return false; }
    for (const a of form.approvers) {
      if (!a.user_id) { toast.error(`L${a.level} approver not selected.`); return false; }
    }
    return true;
  }

  async function saveDraft(submitAfter = false) {
    if (!_validateForm()) return;
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        const r = await api.post("/one", form);
        saved = r.data;
      } else {
        const r = await api.put(`/one/${doc.id}`, form);
        saved = r.data;
      }
      if (submitAfter) {
        const r2 = await api.post(`/one/${saved.id}/submit`);
        saved = r2.data;
        toast.success(`Submitted — voucher ${saved.voucher_number}`);
      } else {
        toast.success(isNew ? "Draft created" : "Draft updated");
      }
      setDoc(saved);
      // Trigger parent list refresh + keep the tab open so user sees the update.
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function deleteDraft() {
    if (!doc) return;
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/one/${doc.id}`);
      toast.success("Draft deleted");
      if (typeof onSaved === "function") onSaved();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function doAction(kind) {
    if (!doc) return;
    setSaving(true);
    try {
      const r = await api.post(`/one/${doc.id}/${kind}`, { remarks });
      setDoc(r.data);
      setRemarks("");
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)}d`);
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind} failed`);
    } finally {
      setSaving(false);
    }
  }

  async function raiseQuery() {
    if (!doc) return;
    if (!queryTarget.user_id) {
      toast.error("Pick a query target."); return;
    }
    if (!queryText.trim()) { toast.error("Query text is required."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/one/${doc.id}/query`, {
        raised_to_user_id: queryTarget.user_id,
        raised_to_level: queryTarget.level,
        question: queryText,
      });
      setDoc(r.data);
      setQueryText("");
      setQueryTarget({ level: 0, user_id: "" });
      toast.success("Query raised");
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Query failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitReply() {
    if (!doc || !doc.active_query_id) return;
    if (!queryReply.trim()) { toast.error("Reply text is required."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/one/${doc.id}/queries/${doc.active_query_id}/reply`,
                                { reply: queryReply });
      setDoc(r.data);
      setQueryReply("");
      toast.success("Reply sent");
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reply failed");
    } finally {
      setSaving(false);
    }
  }

  async function createRevision() {
    if (!doc) return;
    if (!revisionReason.trim()) { toast.error("Revision reason is required."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/one/${doc.id}/create-revision`, { reason: revisionReason });
      toast.success(`Revision R${r.data.revision_no} draft created`);
      setRevisionReason("");
      // Open the new revision in place of this doc
      setDoc(r.data);
      setForm({
        category: r.data.category,
        branch_id: r.data.branch_id,
        branch_name: r.data.branch_name || "",
        subject: r.data.subject,
        body_html: r.data.body_html || "",
        amount: r.data.amount || 0,
        project_id: r.data.project_id || "",
        project_name: r.data.project_name || "",
        approvers: r.data.approvers || [],
        attachments: r.data.attachments || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revision failed");
    } finally {
      setSaving(false);
    }
  }

  async function loadHistory() {
    if (!doc) return;
    try {
      const r = await api.get(`/one/${doc.id}/history`);
      setHistory(r.data || []);
      setShowHistory(true);
    } catch (e) {
      toast.error("Could not load history");
    }
  }

  async function downloadPdf() {
    if (!doc) return;
    try {
      const r = await api.get(`/one/${doc.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const fname = ((doc.voucher_number || doc.id) || "").replace(/\//g, "-");
      a.download = `ONE-${fname}${doc.revision_no ? `-R${doc.revision_no}` : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF export failed");
    }
  }

  async function loadCompare(otherId) {
    if (!doc) return;
    try {
      const r = await api.get(`/one/${doc.id}/compare/${otherId}`);
      setCompareData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load compare");
    }
  }

  const showFormFields = isCreatorOfDraft;
  const currentLevelApprover = (doc?.approvers || []).find(a => a.level === doc?.current_level);
  // "Lower levels" for the raise-query dropdown = strictly below the
  // acting user's own level (which may be higher than doc.current_level
  // when they're using the upper-level skip flow).
  const lowerLevels = (doc?.approvers || []).filter(a =>
    a.level < (myActingLevel || doc?.current_level || 0)
  );

  return (
    <div className="w-full" data-testid="one-detail-page">
      <div className="bg-white border border-slate-200">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold flex items-center gap-2">
              <ClipboardList size={11} />
              {isNew ? "New Note" : "Note Detail"}
              {doc?.voucher_number && (
                <span className="font-mono text-slate-900 tracking-normal">
                  · {doc.voucher_number}
                  {doc.revision_no > 0 && <span className="text-purple-700">R{doc.revision_no}</span>}
                </span>
              )}
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-1 truncate" title={form.subject}>
              {form.subject || <span className="italic text-slate-400">(subject)</span>}
            </h2>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {doc && <StatusRibbon status={doc.status} current={doc.current_level} total={(doc.approvers || []).length} />}
              {doc && (
                <button
                  data-testid="one-history-btn"
                  onClick={loadHistory}
                  className="text-[11px] inline-flex items-center gap-1 text-slate-600 hover:text-[#F97316] border border-slate-300 hover:border-[#F97316] px-2 py-1"
                >
                  <History size={11} /> History
                </button>
              )}
              {doc && doc.status && doc.status !== "Draft" && (
                <button
                  type="button"
                  data-testid="one-pdf-btn"
                  onClick={downloadPdf}
                  className="text-[11px] inline-flex items-center gap-1 text-slate-600 hover:text-[#F97316] border border-slate-300 hover:border-[#F97316] px-2 py-1"
                >
                  <FileDown size={11} /> PDF
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {typeof onViewList === "function" && (
              <button
                type="button"
                data-testid="one-back-to-list"
                onClick={onViewList}
                className="text-[11px] inline-flex items-center gap-1 text-slate-700 hover:text-[#F97316] border border-slate-300 hover:border-[#F97316] px-3 py-1.5 uppercase tracking-wider font-bold"
              >
                Back to List
              </button>
            )}
            {typeof onClose === "function" && (
              <button
                type="button"
                data-testid="one-close-btn"
                onClick={onClose}
                title="Close this tab"
                className="text-slate-500 hover:text-slate-900"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-slate-500 italic">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Approvers strip (existing docs) */}
            {doc && (doc.approvers || []).length > 0 && (
              <ApproversStrip
                approvers={doc.approvers}
                currentLevel={doc.current_level}
                status={doc.status}
                actions={doc.actions || []}
              />
            )}

            {/* Meta grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Category">
                <select
                  data-testid="one-category"
                  disabled={!isCreatorOfDraft || saving}
                  value={form.category}
                  onChange={(e) => setForm(f => ({
                    ...f, category: e.target.value,
                    // clear amount/project when moving away from Financial
                    ...(e.target.value !== "Financial" ? { amount: 0, project_id: "", project_name: "" } : {}),
                  }))}
                  className={inputCls}
                >
                  {Object.keys(meta).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Branch">
                <select
                  data-testid="one-branch"
                  disabled={!isCreatorOfDraft || saving}
                  value={form.branch_id}
                  onChange={(e) => pickBranch(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Select —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
                </select>
              </Field>
              {categoryCfg.project_required && (
                <Field label="Project *">
                  <SearchableSelect
                    testid="one-project"
                    items={projects}
                    value={form.project_id}
                    onChange={(id, row) => setForm(f => ({
                      ...f,
                      project_id: id || "",
                      project_name: row?.project_name || "",
                    }))}
                    getKey={(r) => r.id}
                    getLabel={(r) => (r.project_code ? `${r.project_code} · ` : "") + (r.project_name || "")}
                    getSubLabel={(r) => r.client_name || ""}
                    getSearchText={(r) =>
                      `${r.project_code || ""} ${r.project_name || ""} ${r.client_name || ""}`
                    }
                    placeholder={form.branch_id ? "— Select project —" : "Pick a branch first"}
                    searchPlaceholder="Search by code, name or client…"
                    emptyText="No projects found for this branch"
                    disabled={!isCreatorOfDraft || saving || !form.branch_id}
                  />
                </Field>
              )}
              {categoryCfg.amount_required && (
                <Field label="Approval Amount ₹ *">
                  <input
                    data-testid="one-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={!isCreatorOfDraft || saving}
                    value={form.amount || ""}
                    onChange={(e) => setForm(f => ({ ...f, amount: Number(e.target.value) || 0 }))}
                    className={inputCls}
                  />
                </Field>
              )}
            </div>

            <Field label="Subject *" full>
              <input
                data-testid="one-subject"
                type="text"
                maxLength={300}
                disabled={!isCreatorOfDraft || saving}
                value={form.subject}
                onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Brief one-line summary of the note"
                className={inputCls}
              />
            </Field>

            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-1">
                Note Body
              </div>
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => setForm(f => ({ ...f, body_html: html }))}
                readOnly={!isCreatorOfDraft || saving}
                minHeight={220}
                testid="one-body"
              />
            </div>

            {/* Attachments */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-2 flex items-center justify-between">
                <span>Attachments <span className="text-slate-400">(PDF / XLSX / DOCX / JPG / PNG · max 3 files · 500 KB each)</span></span>
                {isCreatorOfDraft && (form.attachments || []).length < ATTACH_MAX_COUNT && (
                  <label
                    className="text-[11px] inline-flex items-center gap-1 text-[#F97316] hover:text-[#EA580C] cursor-pointer font-bold"
                    data-testid="one-att-upload"
                  >
                    <Upload size={11} /> Upload
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept={MIME_ALLOW.join(",")}
                      onChange={onPickFiles}
                    />
                  </label>
                )}
              </div>
              {(form.attachments || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 px-3 py-3">
                  No attachments.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {form.attachments.map((att, i) => (
                    <AttachmentRow
                      key={i}
                      att={att}
                      canRemove={isCreatorOfDraft && !saving}
                      onRemove={() => removeAttachment(i)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Approvers picker (create/edit-draft) or read-only strip (already shown above) */}
            {isCreatorOfDraft && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-2">
                  Approval Chain (L1 → L5) *
                </div>
                <ApproversPicker
                  branchId={form.branch_id}
                  value={form.approvers}
                  onChange={(v) => setForm(f => ({ ...f, approvers: v }))}
                  disabled={saving}
                />
              </div>
            )}

            {/* Existing actions log */}
            {doc && (doc.actions || []).length > 0 && (
              <Card className="p-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-2">
                  Approval Actions
                </div>
                {doc.actions.map((a, i) => <ActionRow key={i} action={a} idx={i} />)}
              </Card>
            )}

            {/* Query history */}
            {doc && (doc.queries || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-2">
                  Queries
                </div>
                <div className="space-y-2">
                  {doc.queries.map((q, i) => (
                    <QueryRow key={i} q={q} idx={i} approvers={doc.approvers || []}
                      initiatorName={doc.initiated_by_name || doc.initiated_by_email} />
                  ))}
                </div>
              </div>
            )}

            {/* Query reply UI */}
            {isQueryRecipient && doc?.status === "Query Pending" && (
              <Card className="p-4 border-violet-200 bg-violet-50/40" data-testid="one-reply-card">
                <div className="text-[11px] font-bold uppercase tracking-wider text-violet-900 mb-2">
                  Reply to Query
                </div>
                <textarea
                  data-testid="one-reply-text"
                  className={inputCls}
                  rows={3}
                  placeholder="Type your reply…"
                  value={queryReply}
                  onChange={(e) => setQueryReply(e.target.value)}
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    data-testid="one-reply-submit"
                    disabled={saving || !queryReply.trim()}
                    onClick={submitReply}
                    className="inline-flex items-center gap-1 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <Send size={12} /> Send Reply
                  </button>
                </div>
              </Card>
            )}

            {/* Approver actions */}
            {isCurrentApprover && (doc?.status === "Pending" || doc?.status === "Hold" || doc?.status === "Query Replied") && (
              <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="one-action-card">
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900 mb-2">
                  Action Required at L{myActingLevel || doc.current_level}
                  {myActingLevel > doc.current_level && (
                    <span
                      className="ml-2 text-[10px] font-bold uppercase tracking-wider text-purple-800 bg-purple-100 border border-purple-300 px-2 py-0.5"
                      data-testid="one-skip-banner"
                    >
                      Upper-level override — Levels L{doc.current_level}
                      {myActingLevel - doc.current_level > 1 ? `–L${myActingLevel - 1}` : ""}
                      {" "}will be recorded as skipped
                    </span>
                  )}
                </div>
                <Field label="Remarks (optional)">
                  <textarea
                    data-testid="one-remarks"
                    className={inputCls}
                    rows={2}
                    placeholder="Add remarks for the audit trail"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Field>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    type="button"
                    data-testid="one-approve"
                    disabled={saving}
                    onClick={() => doAction("approve")}
                    className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    type="button"
                    data-testid="one-reject"
                    disabled={saving}
                    onClick={() => doAction("reject")}
                    className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <XCircle size={12} /> Reject
                  </button>
                  {doc.status !== "Query Replied" && (
                    <button
                      type="button"
                      data-testid="one-hold"
                      disabled={saving}
                      onClick={() => doAction("hold")}
                      className="inline-flex items-center gap-1 border border-orange-500 text-orange-700 hover:bg-orange-50 px-4 py-2 text-xs uppercase tracking-wider font-bold"
                    >
                      <Pause size={12} /> Hold
                    </button>
                  )}
                </div>

                {/* Raise Query — target = Initiator (always) or any lower-level approver */}
                <div className="mt-4 border-t border-amber-200 pt-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900 mb-2 inline-flex items-center gap-1">
                      <HelpCircle size={11} /> Raise a Query
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        data-testid="one-query-target"
                        className={inputCls}
                        value={queryTarget.user_id ? `${queryTarget.level}|${queryTarget.user_id}` : ""}
                        onChange={(e) => {
                          const [lvl, uid] = e.target.value.split("|");
                          setQueryTarget({ level: Number(lvl) || 0, user_id: uid || "" });
                        }}
                      >
                        <option value="">— Select query target —</option>
                        {doc.initiated_by_user_id && (
                          <option value={`0|${doc.initiated_by_user_id}`}>
                            Initiator · {doc.initiated_by_name || doc.initiated_by_email}
                          </option>
                        )}
                        {lowerLevels.map(a => (
                          <option key={a.level} value={`${a.level}|${a.user_id}`}>
                            L{a.level} · {a.user_name || a.user_email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <textarea
                      data-testid="one-query-text"
                      className={`${inputCls} mt-2`}
                      rows={2}
                      placeholder="Question for the initiator / lower-level approver…"
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        data-testid="one-query-submit"
                        disabled={saving || !queryTarget.user_id || !queryText.trim()}
                        onClick={raiseQuery}
                        className="inline-flex items-center gap-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                      >
                        <HelpCircle size={12} /> Raise Query
                      </button>
                    </div>
                </div>
              </Card>
            )}

            {/* Revision UI */}
            {canCreateRevision && (
              <Card className="p-4 border-purple-200 bg-purple-50/40" data-testid="one-revision-card">
                <div className="text-[11px] font-bold uppercase tracking-wider text-purple-900 mb-2 inline-flex items-center gap-1">
                  <GitBranch size={11} /> Create Revision
                </div>
                <div className="text-[11px] text-purple-800 mb-2">
                  This is an Approved <b>Policy Making</b> note. Creating a revision spawns a new draft;
                  once approved it will supersede this version.
                </div>
                <Field label="Revision Reason *">
                  <input
                    data-testid="one-revision-reason"
                    type="text"
                    maxLength={500}
                    value={revisionReason}
                    onChange={(e) => setRevisionReason(e.target.value)}
                    placeholder="Why is this policy being revised?"
                    className={inputCls}
                  />
                </Field>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    data-testid="one-revision-create"
                    disabled={saving || !revisionReason.trim()}
                    onClick={createRevision}
                    className="inline-flex items-center gap-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <GitBranch size={12} /> Spawn Revision Draft
                  </button>
                </div>
              </Card>
            )}

            {/* History drawer */}
            {showHistory && (
              <Card className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold">
                    Version History
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowHistory(false)}
                    className="text-slate-500 hover:text-slate-900"
                    data-testid="one-history-close"
                  >
                    <X size={13} />
                  </button>
                </div>
                {history.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">Only one version — no revisions yet.</div>
                ) : (
                  <div className="space-y-1">
                    {history.map((h, i) => (
                      <div key={h.id}
                           className={`flex items-center justify-between border px-3 py-2 text-xs ${
                             h.id === doc?.id
                               ? "border-[#F97316] bg-orange-50"
                               : h.is_active
                                 ? "border-emerald-300 bg-emerald-50"
                                 : "border-slate-200 bg-slate-50"
                           }`}>
                        <div className="min-w-0 flex-1">
                          <span className="font-mono font-bold">{h.voucher_number || "—"}</span>
                          {" · "}
                          <span className="text-slate-600">R{h.revision_no}</span>
                          {" · "}
                          <span className="text-slate-600">{h.status}</span>
                          {h.is_active && <span className="ml-2 text-emerald-800 text-[10px] font-bold uppercase">Active</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-[10px] text-slate-500">
                            {h.approved_at ? new Date(h.approved_at).toLocaleDateString() : new Date(h.created_at).toLocaleDateString()}
                          </div>
                          {h.id !== doc?.id && (
                            <button
                              type="button"
                              data-testid={`one-compare-${h.id}`}
                              onClick={() => loadCompare(h.id)}
                              className="inline-flex items-center gap-1 text-[10px] text-slate-700 hover:text-[#F97316] border border-slate-300 hover:border-[#F97316] px-2 py-1 uppercase tracking-wider font-bold"
                            >
                              <GitCompare size={11} /> Compare
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Side-by-side compare panel (Phase 3) */}
                {compareData && (
                  <div className="mt-4 border-t border-slate-200 pt-4" data-testid="one-compare-panel">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold">
                        Compare Versions
                      </div>
                      <button
                        type="button"
                        onClick={() => setCompareData(null)}
                        className="text-slate-500 hover:text-slate-900"
                        data-testid="one-compare-close"
                      >
                        <X size={13} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {["left", "right"].map((side) => {
                        const d = compareData[side];
                        if (!d) return null;
                        return (
                          <div
                            key={side}
                            data-testid={`one-compare-${side}`}
                            className={`border ${side === "left" ? "border-slate-300 bg-slate-50/60" : "border-emerald-300 bg-emerald-50/40"} p-3`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">
                                {side === "left" ? "Previous" : "Newer / Current"}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                R{d.revision_no}{d.is_active ? " · Active" : ""}
                              </div>
                            </div>
                            <div className="text-[11px] font-mono font-bold text-slate-900 mb-1">
                              {d.voucher_number || "(no voucher)"}
                            </div>
                            <div className="text-xs font-bold text-slate-900 mb-1 break-words">
                              {d.subject || "—"}
                            </div>
                            {d.revision_reason && (
                              <div className="text-[10px] text-purple-900 italic mb-2 border-l-2 border-purple-400 pl-2">
                                Revision reason: {d.revision_reason}
                              </div>
                            )}
                            <div
                              className="rte-content text-xs text-slate-700 border-t border-slate-200 pt-2 mt-2 max-h-[420px] overflow-auto"
                              dangerouslySetInnerHTML={{ __html: d.body_html || "<p class='italic text-slate-400'>(empty body)</p>" }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Footer actions */}
            {isCreatorOfDraft && (
              <div className="border-t border-slate-200 pt-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  {doc && (
                    <button
                      type="button"
                      data-testid="one-delete-draft"
                      disabled={saving}
                      onClick={deleteDraft}
                      className="inline-flex items-center gap-1 border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-2 text-xs uppercase tracking-wider font-bold"
                    >
                      <Trash2 size={12} /> Delete Draft
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="one-save-draft"
                    disabled={saving}
                    onClick={() => saveDraft(false)}
                    className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-100 text-slate-800 px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <Save size={12} /> Save Draft
                  </button>
                  <button
                    type="button"
                    data-testid="one-submit"
                    disabled={saving}
                    onClick={() => saveDraft(true)}
                    className="inline-flex items-center gap-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
                  >
                    <Send size={12} /> Save & Submit
                  </button>
                </div>
              </div>
            )}

            {/* Superseded / Terminal — read-only notice */}
            {doc && STATUS_TERMINAL.has(doc.status) && !canCreateRevision && (
              <div className="text-[11px] text-slate-500 italic border-t border-slate-200 pt-3">
                This note is <b>{doc.status}</b> and is read-only.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
